// Fake data for testing when testEnv is true
// Contains 100 static form records with complete structure

import { processStages } from "./userWidgets";

const stageKeys = Object.keys(processStages);
const statuses = ['در حال مذاکره', 'منصرف شده', 'پیش قرارداد', 'قرارداد'];

// Generate realistic Persian names
const firstNames = ['علی', 'محمد', 'حسن', 'حسین', 'رضا', 'احمد', 'مهدی', 'امیر', 'سعید', 'فرهاد', 'کامران', 'بهرام', 'آرش', 'پویا', 'نیما'];
const lastNames = ['احمدی', 'محمدی', 'حسینی', 'رضایی', 'کریمی', 'موسوی', 'نوری', 'صادقی', 'جعفری', 'زمانی', 'کاظمی', 'شریفی', 'امینی', 'باقری', 'طاهری'];
const cities = ['تهران', 'اصفهان', 'شیراز', 'مشهد', 'تبریز', 'کرج', 'قم', 'اهواز', 'کرمان', 'رشت'];
const streets = ['خیابان ولیعصر', 'خیابان انقلاب', 'خیابان آزادی', 'خیابان جمهوری', 'خیابان کارگر', 'خیابان فاطمی', 'خیابان سعادت آباد', 'خیابان پاسداران'];
const horseNames = ['شاهین', 'طوفان', 'آذرخش', 'سیمرغ', 'رعد', 'برق', 'باد', 'توفان', 'آتش', 'یاقوت', 'الماس', 'زر', 'نقره', 'طلایی', 'آبی'];
const colors = ['قرمز', 'سیاه', 'سفید', 'خاکستری', 'قهوه‌ای', 'طلایی', 'نقره‌ای', 'آبی', 'سبز', 'زرد'];

// Keep local role previews stable across reloads so visual and regression tests
// always exercise the same records.
let previewSeed = 20260729;
const previewNow = Date.UTC(2026, 6, 29, 9, 0, 0);
const seededRandom = () => {
    previewSeed = (previewSeed * 1664525 + 1013904223) % 4294967296;
    return previewSeed / 4294967296;
};

// Helper function to generate a deterministic date within range
const randomDate = (daysAgo) => {
    const randomMs = seededRandom() * daysAgo * 24 * 60 * 60 * 1000;
    return previewNow - randomMs;
};

// Helper function to generate random phone number
const randomPhone = () => {
    const prefix = ['0912', '0913', '0914', '0915', '0916', '0917', '0918', '0919', '0921', '0922', '0930', '0935', '0936', '0937', '0938', '0939'];
    return prefix[Math.floor(seededRandom() * prefix.length)] + Math.floor(seededRandom() * 10000000).toString().padStart(7, '0');
};

// Helper function to generate random national ID
const randomNationalId = () => {
    return Math.floor(seededRandom() * 9000000000) + 1000000000;
};

// Helper function to get random item from array
const randomItem = (arr) => arr[Math.floor(seededRandom() * arr.length)];

// Generate 100 static form records
const generateFakeForms = () => {
    const fakeForms = [];
    
    for (let i = 0; i < 100; i++) {
        const formNumber = i + 1;
        const statusIndex = Math.floor(i / 25); // Distribute: 0-24: در حال مذاکره, 25-49: منصرف شده, 50-74: پیش قرارداد, 75-99: قرارداد
        const lastFollowUpResult = statuses[statusIndex];
        const isContract = lastFollowUpResult === 'قرارداد';
        
        // Distribute stages more realistically
        let currentStageIndex;
        if (isContract) {
            // Contracts should have more advanced stages
            const progress = (i - 75) / 24; // 0 to 1
            currentStageIndex = Math.min(
                Math.floor(progress * stageKeys.length * 0.7), // Up to 70% of stages
                stageKeys.length - 1
            );
        } else {
            // Non-contracts should have earlier stages
            currentStageIndex = Math.floor(seededRandom() * Math.min(5, stageKeys.length));
        }
        const currentStage = stageKeys[currentStageIndex];
        
        // Generate customer info
        const customerName = `${randomItem(firstNames)} ${randomItem(lastNames)}`;
        const nationalId = randomNationalId();
        const phoneNumber = randomPhone();
        const city = randomItem(cities);
        const street = randomItem(streets);
        const address = `${city}، ${street}، پلاک ${Math.floor(seededRandom() * 200) + 1}`;
        const postalCode = Math.floor(seededRandom() * 9000000) + 1000000;
        
        // Generate dates
        const cDate = randomDate(90); // Created within last 90 days
        const uDate = randomDate(30); // Updated within last 30 days
        const formDate = randomDate(90);
        const birthDate = randomDate(365 * 30); // Customer birth date (around 30 years ago)
        
        // Generate female horse info
        const mareName = randomItem(horseNames) + ' ماده';
        const model = randomItem(horseNames);
        const modelDesc = `توضیحات مدل ${model}`;
        const modelDad = randomItem(horseNames) + ' پدر';
        const modelDadDesc = `توضیحات پدر ${modelDad}`;
        const modelMom = randomItem(horseNames) + ' مادر';
        const modelMomDesc = `توضیحات مادر ${modelMom}`;
        const modelMomDad = randomItem(horseNames) + ' پدربزرگ';
        const modelMomDadDesc = `توضیحات پدربزرگ ${modelMomDad}`;
        const colour = randomItem(colors);
        const colorDesc = `توضیحات رنگ ${colour}`;
        const birthDateFemale = randomDate(365 * 10); // Female horse birth date (around 10 years ago)
        const microChipNumber = `CHIP-${Math.floor(seededRandom() * 1000000)}`;
        
        // Generate male horse info
        const stallionName = randomItem(horseNames) + ' نر';
        const modelMale = randomItem(horseNames);
        const modelMaleDesc = `توضیحات مدل نر ${modelMale}`;
        const modelMaleDad = randomItem(horseNames) + ' پدر نر';
        const modelMaleDadDesc = `توضیحات پدر نر ${modelMaleDad}`;
        const modelMaleMom = randomItem(horseNames) + ' مادر نر';
        const modelMaleMomDesc = `توضیحات مادر نر ${modelMaleMom}`;
        const modelMaleMomDad = randomItem(horseNames) + ' پدربزرگ نر';
        const colourMale = randomItem(colors);
        const colorDescMale = `توضیحات رنگ نر ${colourMale}`;
        const birthDateMale = randomDate(365 * 12); // Male horse birth date (around 12 years ago)
        const microChipNumberMale = `CHIP-M-${Math.floor(seededRandom() * 1000000)}`;
        
        // Generate IDs for relationships (these would be populated in real data)
        const veterinarianId = `vet_${Math.floor(seededRandom() * 10) + 1}`;
        const customerId = `customer_${Math.floor(seededRandom() * 20) + 1}`;
        const appletId = `applet_${Math.floor(seededRandom() * 5) + 1}`;
        const creatorId = `creator_${Math.floor(seededRandom() * 10) + 1}`;
        const colourId = `color_${Math.floor(seededRandom() * 10) + 1}`;
        const colourMaleId = `color_${Math.floor(seededRandom() * 10) + 1}`;
        
        // Build form object
        const form = {
            _id: `fake_form_${formNumber}`,
            frame: 'forms',
            page: 'forms',
            cDate: cDate,
            uDate: uDate,
            values: {
                // Basic customer info
                customerName: customerName,
                nationalId: nationalId.toString(),
                birthDate: birthDate,
                address: address,
                postalCode: postalCode.toString(),
                phoneNumber: phoneNumber,
                date: formDate,
                lastFollowUpResult: lastFollowUpResult,
                
                // Form ID
                formId: `FORM-${String(formNumber).padStart(4, '0')}`,
                
                // Female section
                model: model,
                modelDesc: modelDesc,
                modelDad: modelDad,
                modelDadDesc: modelDadDesc,
                modelMom: modelMom,
                modelMomDesc: modelMomDesc,
                modelMomDad: modelMomDad,
                modelMomDadDesc: modelMomDadDesc,
                colour: colourId, // This would be populated with actual color record
                colorDesc: colorDesc,
                birthDateFemale: birthDateFemale,
                microChipNumber: microChipNumber,
                
                // Male section
                modelMale: modelMale, // Could be ID or name in real scenario
                modelMaleDesc: modelMaleDesc,
                modelMaleDad: modelMaleDad,
                modelMaleDadDesc: modelMaleDadDesc,
                modelMaleMom: modelMaleMom,
                modelMaleMomDesc: modelMaleMomDesc,
                modelMaleMomDad: modelMaleMomDad,
                colourMale: colourMaleId, // This would be populated with actual color record
                colorDescMale: colorDescMale,
                birthDateMale: birthDateMale,
                microChipNumberMale: microChipNumberMale,
                
                // Relationships (IDs that would be populated)
                veterinarian: veterinarianId,
                customer: customerId,
                
                // Current status (only for contracts)
                currentStatus: isContract ? {
                    key: currentStage,
                    value: currentStage,
                    date: uDate,
                    description: `وضعیت فعلی: ${processStages[currentStage]?.name || currentStage}`
                } : null,
                
                // Legacy field for compatibility
                currentStage: currentStage,
                mareName: mareName,
                stallionName: stallionName,
            },
            
            // Relationships at root level (for populates)
            applet: appletId,
            creator: creatorId,
            
            // Populated fields (would be populated in real scenario)
            'values.colour': colourId,
            'values.colourMale': colourMaleId,
            'values.veterinarian': veterinarianId,
            'values.customer': customerId,
        };
        
        fakeForms.push(form);
    }
    
    return fakeForms;
};

export const fakeFormsData = generateFakeForms();
